<template lang="pug">
//- Backend health — appears only when a recent transient error is
//- known.  The autoclear-after-2-min behaviour is driven by the
//- store's `isRecent(now)` getter combined with the `now` tick
//- below, so the badge disappears on its own once the backend
//- calms down.  Operators can also dismiss manually.
div(v-if='visible' style='display: flex; align-items: center; gap: 7px; flex-shrink: 0;')
    span(style='font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,180,120,0.9); text-transform: uppercase;') Backend
    div(:style='dotStyle' style='width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;')
    span(
        :title='summary ?? ""'
        style='color: rgba(255,220,180,0.85); max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
    ) {{ summary }}
    button(
        type='button'
        @click='dismiss'
        style='background: none; border: none; color: rgba(255,255,255,0.35); font-size: 12px; cursor: pointer; padding: 0 4px;'
        title='Dismiss'
    ) ✕
div(v-if='visible' style='width: 1px; height: 14px; background: rgba(255,255,255,0.12); flex-shrink: 0; margin: 0 12px;')
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useBackendHealthStore } from '@/store/storeBackendHealth';

const store = useBackendHealthStore();

// Reactive `now` — ticks every 10 s so the "is-recent" check
// re-evaluates and the badge visibly disappears after the store's
// autoclear window (2 min).  A shorter tick would refresh more
// often but isn't visually meaningful.
const now = ref<number>(Date.now());
const timer = window.setInterval(() => { now.value = Date.now(); }, 10_000);
onBeforeUnmount(() => window.clearInterval(timer));

const visible = computed<boolean>(() => store.isRecent(now.value));
const summary = computed<string | null>(() => store.summary);
const kind = computed<string>(() => store.latest?.kind ?? 'unknown');

const dotStyle = computed<Record<string, string>>(() => {
    // Amber for the transient-error class we care about most
    // (server / network / timeout); red for client-side rejections
    // (which usually mean the request will fail again the same way).
    if (kind.value === 'client') {
        return { background: '#ef4444', boxShadow: '0 0 4px #ef444488' };
    }
    return { background: '#f59e0b', boxShadow: '0 0 4px #f59e0b88' };
});

function dismiss(): void {
    store.clear();
}
</script>
